import { useId, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Camera, Loader2, Package, ShieldCheck, X } from 'lucide-react'

import {
  addProtectionSerial,
  portalProjectQueryOptions,
  type PortalProject,
  type PortalProtectedUnit,
} from '@/api/client'
import { Button } from '@/components/ui/button'
import { formatFullDay } from '@/lib/tracker'
import { cn } from '@/lib/utils'

/** The "Protect your equipment" section: each unit a plan covers, and its serial number (USA-1164). */
export function ProtectionPlans({ token, units }: { token: string; units: PortalProtectedUnit[] }) {
  const headingId = useId()
  const editable = units.some((unit) => unit.can_edit)
  return (
    <section aria-labelledby={headingId}>
      <h2 id={headingId} className="text-xl font-semibold tracking-tight">
        Protect your equipment
      </h2>
      {editable ? (
        <p className="text-muted-foreground mt-1 text-[15px]">
          Add each unit&apos;s serial number from its rating plate to start its plan.
        </p>
      ) : null}
      <ul className="bg-card mt-4 divide-y overflow-hidden rounded-2xl border">
        {units.map((unit) => (
          <ProtectedUnitRow key={unit.id} token={token} unit={unit} />
        ))}
      </ul>
    </section>
  )
}

function ProtectedUnitRow({ token, unit }: { token: string; unit: PortalProtectedUnit }) {
  const meta = [unit.model, unit.of > 1 ? `Unit ${unit.unit} of ${unit.of}` : null]
    .filter(Boolean)
    .join(' · ')
  return (
    <li className="grid grid-cols-[3.5rem_minmax(0,1fr)] items-start gap-4 px-4 py-4 sm:px-5">
      <UnitImage url={unit.image_url} />
      <div className="min-w-0">
        <p className="line-clamp-2 text-sm leading-snug font-medium">{unit.name}</p>
        {meta ? <p className="text-muted-foreground mt-0.5 text-xs">{meta}</p> : null}
        <div className="mt-3">
          {unit.can_edit ? <SerialForm token={token} unit={unit} /> : <UnitStatus unit={unit} />}
        </div>
      </div>
    </li>
  )
}

function UnitStatus({ unit }: { unit: PortalProtectedUnit }) {
  if (unit.status === 'active') {
    return (
      <div>
        <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700">
          <ShieldCheck aria-hidden="true" className="size-[18px]" />
          Protected through {formatFullDay(unit.covered_through)}
        </p>
        <p className="text-muted-foreground mt-1 text-xs">
          Serial <span className="text-foreground font-mono">{unit.serial}</span>
          {unit.policy_number ? (
            <>
              {' · '}Policy <span className="text-foreground font-mono">{unit.policy_number}</span>
            </>
          ) : null}
        </p>
      </div>
    )
  }
  if (unit.status === 'activating') {
    return (
      <div role="status">
        <p className="inline-flex items-center gap-1.5 text-sm font-medium">
          <Loader2
            aria-hidden="true"
            className="text-usa-blue size-4 animate-spin motion-reduce:animate-none"
          />
          Activating your plan
        </p>
        <p className="text-muted-foreground mt-1 text-xs">
          Serial <span className="text-foreground font-mono">{unit.serial}</span>
        </p>
      </div>
    )
  }
  return <p className="text-muted-foreground text-sm">Plan cancelled</p>
}

function SerialForm({ token, unit }: { token: string; unit: PortalProtectedUnit }) {
  const id = useId()
  const queryClient = useQueryClient()
  const queryKey = portalProjectQueryOptions(token).queryKey
  const [serial, setSerial] = useState(unit.status === 'check_serial' ? (unit.serial ?? '') : '')
  const [photo, setPhoto] = useState<File | null>(null)
  const save = useMutation({
    mutationFn: () => addProtectionSerial(token, unit.id, serial.trim(), photo),
    onSuccess: (saved) => {
      queryClient.setQueryData<PortalProject>(queryKey, (project) =>
        project
          ? {
              ...project,
              orders: project.orders.map((order) => ({
                ...order,
                protection: order.protection.map((row) => (row.id === saved.id ? saved : row)),
              })),
            }
          : project,
      )
      void queryClient.invalidateQueries({ queryKey })
    },
  })
  const refused = unit.status === 'check_serial' && !save.isError
  const message = save.isError
    ? save.error.message
    : refused
      ? "We couldn't activate this serial number. Check it against the rating plate and try again."
      : null

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        if (serial.trim()) save.mutate()
      }}
    >
      <label htmlFor={`${id}-serial`} className="text-xs font-medium">
        Serial number
      </label>
      <div className="mt-1.5 flex flex-col gap-2 sm:flex-row">
        <input
          id={`${id}-serial`}
          value={serial}
          onChange={(event) => setSerial(event.target.value)}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={50}
          aria-invalid={message ? true : undefined}
          aria-describedby={message ? `${id}-message` : undefined}
          className={cn(
            'border-input bg-background h-11 w-full min-w-0 rounded-lg border px-3 font-mono text-[15px] uppercase outline-none sm:flex-1',
            'placeholder:text-muted-foreground placeholder:font-sans placeholder:normal-case',
            'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
            'aria-invalid:border-rose-600 aria-invalid:ring-rose-600/15',
          )}
        />
        <div className="flex gap-2">
          <PhotoPicker id={`${id}-photo`} photo={photo} onChange={setPhoto} />
          <Button
            type="submit"
            className="h-11 flex-1 px-5 sm:flex-none"
            disabled={!serial.trim() || save.isPending}
          >
            {save.isPending ? (
              <>
                <Loader2 aria-hidden="true" className="animate-spin motion-reduce:animate-none" />
                Activating
              </>
            ) : (
              'Activate plan'
            )}
          </Button>
        </div>
      </div>
      {message ? (
        <p id={`${id}-message`} role="alert" className="mt-2 text-sm text-rose-700">
          {message}
        </p>
      ) : null}
    </form>
  )
}

function PhotoPicker({
  id,
  photo,
  onChange,
}: {
  id: string
  photo: File | null
  onChange: (photo: File | null) => void
}) {
  if (photo) {
    return (
      <span className="bg-muted inline-flex h-11 max-w-[11rem] items-center gap-1.5 rounded-lg pr-1 pl-3 text-sm">
        <Camera aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
        <span className="truncate">{photo.name}</span>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="hover:bg-background focus-visible:ring-ring/50 grid size-9 shrink-0 place-items-center rounded-md outline-none focus-visible:ring-[3px]"
        >
          <X aria-hidden="true" className="size-4" />
          <span className="sr-only">Remove photo</span>
        </button>
      </span>
    )
  }
  return (
    <>
      <input
        id={id}
        type="file"
        accept="image/*"
        className="peer sr-only"
        onChange={(event) => onChange(event.target.files?.[0] ?? null)}
      />
      <label
        htmlFor={id}
        className="bg-background hover:bg-accent peer-focus-visible:ring-ring/50 inline-flex h-11 shrink-0 cursor-pointer items-center gap-2 rounded-lg border px-3 text-sm font-medium shadow-xs peer-focus-visible:ring-[3px]"
      >
        <Camera aria-hidden="true" className="size-4" />
        Add photo<span className="sr-only"> of the rating plate</span>
      </label>
    </>
  )
}

function UnitImage({ url }: { url: string | null }) {
  const [failed, setFailed] = useState(false)
  if (!url || failed) {
    return (
      <span className="bg-muted grid size-14 place-items-center rounded-lg">
        <Package aria-hidden="true" className="text-muted-foreground size-5" />
      </span>
    )
  }
  return (
    <img
      alt=""
      src={url}
      loading="lazy"
      onError={() => setFailed(true)}
      className="size-14 rounded-lg border bg-white object-contain p-1"
    />
  )
}
